import React from 'react'
import { withTranslation } from 'react-i18next'
import {
	isMetricCountedForKrWeighting,
	sumEnteredKrWeightPercents,
	krNormalizedObjectivePercent,
	KR_WEIGHT_MULTIPLIER_INPUT_DECIMAL_PLACES,
} from '../../../utils/krWeightageScale'

/**
 * Allow digits + one decimal point; truncate fractional part to {@link KR_WEIGHT_MULTIPLIER_INPUT_DECIMAL_PLACES}.
 */
function sanitizeSharePercentRaw(rawInput) {
	let raw = String(rawInput).replace(/[^0-9.]/g, '')
	const firstDot = raw.indexOf('.')
	if (firstDot !== -1) {
		raw =
			raw.slice(0, firstDot + 1) + raw.slice(firstDot + 1).replace(/\./g, '')
	}
	if (raw === '' || raw === '.') {
		return ''
	}
	if (firstDot !== -1) {
		const intAndDot = raw.slice(0, firstDot + 1)
		const frac = raw.slice(firstDot + 1).slice(0, KR_WEIGHT_MULTIPLIER_INPUT_DECIMAL_PLACES)
		raw = intAndDot + frac
	}
	return raw
}

/**
 * @param {{ item: unknown[] }} props
 */
function SharePercentInput(props) {
	const { krId, krWeightCal, value, ariaLabel } = props

	const handleChange = (event) => {
		const next = sanitizeSharePercentRaw(event.target.value)
		krWeightCal(krId, next)
	}

	return (
		<td className="multiplierDiv">
			<input
				id={'kr-share-' + krId}
				type="text"
				inputMode="decimal"
				className="krMultiplierContent krWeightMultiplierInput"
				autoComplete="off"
				onChange={handleChange}
				value={typeof value === 'string' ? value : ''}
				aria-label={ariaLabel}
			/>
		</td>
	)
}

class KrWeighting extends React.Component {
	computeIncludedPercentSum() {
		const { item, krWeightage } = this.props
		return sumEnteredKrWeightPercents(
			Array.isArray(item) ? item : [],
			krWeightage || {},
		)
	}

	sharePercentDisplayValue(metric) {
		const { krWeightage } = this.props
		const p = krWeightage[metric.id]?.percent
		if (p === '' || p === null || typeof p === 'undefined') {
			return ''
		}
		return String(p)
	}

	normalizedPercentLabel(metric, includedSum) {
		const { t, krWeightage } = this.props
		const p = krWeightage[metric.id]?.percent
		if (
			p === '' ||
			p === null ||
			typeof p === 'undefined' ||
			!Number.isFinite(includedSum) ||
			includedSum <= 0
		) {
			return { text: '', aria: `${metric.name}: ${t('Weighting')}` }
		}
		const entered = parseFloat(String(p))
		const norm = krNormalizedObjectivePercent(entered, includedSum)
		if (norm === null) {
			return { text: '', aria: `${metric.name}: ${t('Weighting')}` }
		}
		const text = `${norm}%`
		return {
			text,
			aria: `${metric.name}: ${t('Weighting')} ${text}`,
		}
	}

	render() {
		const {
			t,
			krWeightCal,
			krWeightSaveAttempted,
			krWeightValidationErrors,
		} = this.props

		const krList = Array.isArray(this.props.item) ? this.props.item : []

		const includedSum = this.computeIncludedPercentSum()

		return (
			<div className="aE-modal-content">
				<table
					className="headWeigtContent "
					aria-labelledby="kr-weightage-modal-label"
					key={this.props.addOkrData.goalDetails.id}
				>
					<thead>
						<tr className="firstWsDiv">
							<th scope="col" className=" krHeadWeight">
								<span>{t('KEY RESULT')}</span>
							</th>
							<th scope="col" className=" krTargetHeadWe">
								<span>{t('TARGET')}</span>
							</th>
							<th scope="col" className=" krMultiplier">
								<span>{t('MULTIPLIER')}</span>
								<span className="screen-reader-only">
									{' '}
									{t(
										'Enter up to {{maxDecimals}} decimal places per multiplier.',
										{ maxDecimals: KR_WEIGHT_MULTIPLIER_INPUT_DECIMAL_PLACES },
									)}
								</span>
							</th>
							<th scope="col" className=" krMultiplier krNormalizedHead">
								<span>{t('WEIGHTING')}</span>
								<span className="screen-reader-only">
									{' '}
									{t(
										'Objective weighting from your entered multipliers (each value divided by the sum of entered values).',
									)}
								</span>
							</th>
						</tr>
					</thead>
					<tbody
						className="parentKr"
						style={
							krList.length > 4 ? { marginRight: '-10px' } : {}
						}
					>
						{krList.map((metric) => {
							if (!isMetricCountedForKrWeighting(metric)) {
								return null
							}
							const normLabel = this.normalizedPercentLabel(
								metric,
								includedSum,
							)
							return (
								<React.Fragment key={metric.id}>
									<div role="presentation" className="krweightLine" />
									<tr
										id={'kr-wrapper-' + metric.id}
										className="firstWsDiv paddingBtm krMetricWeiData kr_id"
									>
										<td className="krHeadWeightContent">{metric.name}</td>
										<td className="krTargetHeadWeContent">{metric.target}</td>
										<SharePercentInput
											krId={metric.id}
											krWeightCal={krWeightCal}
											value={this.sharePercentDisplayValue(metric)}
											ariaLabel={`${metric.name}: ${t('Multiplier')}`}
										/>
										<td
											className="normalizedShareDiv krNormalizedCell"
											style={{
												fontVariantNumeric: 'tabular-nums',
												color: '#172b4d',
												fontWeight: 600,
											}}
										>
											<span
												className="krNormalizedDisplay"
												aria-label={normLabel.aria}
											>
												{normLabel.text}
											</span>
										</td>
									</tr>
								</React.Fragment>
							)
						})}
					</tbody>
					{krWeightSaveAttempted &&
						Array.isArray(krWeightValidationErrors) &&
						krWeightValidationErrors.length > 0 && (
						<tfoot>
							{krWeightValidationErrors.map((msg, idx) => (
								<tr className="firstWsDiv" key={String(idx)}>
									<td
										colSpan={4}
										style={{ color: '#bf2600', fontSize: 13, paddingTop: 4 }}
									>
										{msg}
									</td>
								</tr>
							))}
						</tfoot>
					)}
				</table>
			</div>
		)
	}
}

export default withTranslation()(KrWeighting)
